import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Plug, Trash2 } from "lucide-react";

export default function Integrations() {
  const [integration, setIntegration] = useState(null);
  const [provider, setProvider] = useState("getresponse");
  const [apiKey, setApiKey] = useState("");
  const [listId, setListId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/integrations").then((r) => setIntegration(r.data.integration)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey.trim()) return toast.error("API key required");
    setSaving(true);
    try {
      await api.post("/integrations", { provider, api_key: apiKey, list_id: listId });
      toast.success("Integration connected");
      setApiKey(""); setListId("");
      load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Disconnect integration?")) return;
    await api.delete("/integrations");
    toast.success("Disconnected");
    load();
  };

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-cap mb-2">// Autoresponder</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Integrations</h1>
        <p className="text-zinc-600 mt-2 max-w-xl">Connect your email autoresponder to auto-sync new leads. <strong>VibeLead always stores leads in your database first</strong>, even if the autoresponder API is down.</p>
      </div>

      {integration && (
        <div className="card-brutal mb-6 bg-zinc-50" data-testid="connected-integration">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-cap mb-1">Connected</div>
              <div className="font-display font-bold text-xl flex items-center gap-2"><Plug size={18} className="text-[#FF4F00]" /> {integration.provider.toUpperCase()}</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">Key: {integration.api_key} · List: {integration.list_id || "—"}</div>
            </div>
            <button onClick={remove} className="btn-brutal-outline !px-3 !py-2 text-sm" data-testid="disconnect-btn"><Trash2 size={14} /> Disconnect</button>
          </div>
        </div>
      )}

      <div className="card-brutal max-w-xl">
        <div className="font-display font-bold text-lg mb-4">{integration ? "Replace integration" : "Connect new integration"}</div>
        <div className="space-y-4">
          <div>
            <label className="label-cap block mb-2">Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className="input-brutal" data-testid="provider-select">
              <option value="getresponse">GetResponse</option>
              <option value="aweber" disabled>AWeber — Coming soon (OAuth required)</option>
            </select>
            {provider === "aweber" && (
              <div className="text-xs text-zinc-500 mt-2">AWeber requires OAuth, which isn't wired yet. Use GetResponse in the meantime — it works with a static API key.</div>
            )}
          </div>
          <div>
            <label className="label-cap block mb-2">API Key</label>
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="input-brutal" placeholder="Paste API key" data-testid="api-key-input" />
          </div>
          <div>
            <label className="label-cap block mb-2">List / Campaign ID</label>
            <input value={listId} onChange={(e) => setListId(e.target.value)} className="input-brutal" placeholder="e.g. abc123" data-testid="list-id-input" />
          </div>
          <button onClick={save} disabled={saving} className="btn-brutal w-full" data-testid="save-integration-btn">
            {saving ? "Saving…" : "Connect"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
